import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'

import { useDataUpload } from '@app/modules/Home/Data/hooks/useDataUpload'
import { DATASETS_QUERY_KEY } from '@app/modules/Home/Data/hooks/useFetchDatasets'
import { useGdalExtract, MAX_GDAL_SIZE } from '@app/modules/Home/Data/hooks/useGdalExtract'
import {
  DataUploadFormData,
  DataUploadSchema,
  ALLOWED_METRIC_TYPES,
} from '@app/modules/Home/Data/types'
import { extractGdalInfo } from '@app/modules/Home/Data/utils/gdal.utils'
import { groupFiles } from '@app/modules/Home/Data/utils/fileGroup.utils'
import { toast } from '@app/components/ui/Toast/toast'

export const useUploadForm = () => {
  const inputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const { upload, uploading, progress, status, errorMessage } = useDataUpload()
  const gdal = useGdalExtract()

  const [activeStep, setActiveStep] = useState(0)
  const [datasetName, setDatasetName] = useState('')
  const [datasetId, setDatasetId] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    resetField,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<DataUploadFormData>({
    resolver: zodResolver(DataUploadSchema),
  })

  const fileList = watch('file')
  const selectedFiles = fileList instanceof FileList ? Array.from(fileList) : []
  const geometryType = watch('geometryType')

  const handleGeometryChange = (
    field: { onChange: (v: string | undefined) => void },
    value: string,
  ) => {
    const newGeometry = value === '' ? undefined : (value as 'line' | 'point' | 'polygon')
    field.onChange(newGeometry)
    const currentMetricType = getValues('metricType')
    if (
      newGeometry &&
      currentMetricType &&
      !ALLOWED_METRIC_TYPES[newGeometry].includes(currentMetricType)
    ) {
      resetField('metricType')
    }
  }

  const handleReset = () => {
    reset()
    if (inputRef.current) inputRef.current.value = ''
    gdal.reset()
    setActiveStep(0)
    setDatasetName('')
    setDatasetId(null)
  }

  const onSubmit = async (data: DataUploadFormData) => {
    const { primary, allFiles } = groupFiles(Array.from(data.file))
    try {
      const fileTooLarge = primary.size > MAX_GDAL_SIZE
      gdal.prepareParallel(fileTooLarge)

      const [uploadResult, gdalResult] = await Promise.allSettled([
        upload(allFiles, {
          name: data.name,
          description: data.description,
          source: data.source || undefined,
          spatialExtent: data.spatialExtent || undefined,
          icon: data.icon ? `s3://${data.icon}` : undefined,
          displaySource: data.displaySource || undefined,
          type: data.type || undefined,
          utility: data.utility || undefined,
          geometryType: data.geometryType,
          lineSegmentLength: data.lineSegmentLength,
          polygonSegmentArea: data.polygonSegmentArea,
          changeToPoint: data.changeToPoint,
          unit: data.unit || undefined,
          metricType: data.metricType,
          license: data.license,
        }),
        !fileTooLarge ? extractGdalInfo(allFiles) : Promise.resolve(null),
      ])

      if (uploadResult.status === 'rejected') {
        Sentry.captureException(uploadResult.reason)
        toast({ title: 'Upload failed', type: 'error', position: 'top-right', duration: 8000 })
        return
      }

      if (gdalResult.status === 'rejected') Sentry.captureException(gdalResult.reason)
      gdal.applySettledResult(fileTooLarge, gdalResult)

      toast({ title: 'Upload complete', type: 'success', position: 'top-right', duration: 8000 })
      queryClient.invalidateQueries({ queryKey: DATASETS_QUERY_KEY })
      setDatasetName(data.name)
      setDatasetId(uploadResult.value.library_id)
      setActiveStep(1)
    } catch (e) {
      Sentry.captureException(e)
    }
  }

  return {
    inputRef,
    register,
    handleSubmit,
    control,
    errors,
    isSubmitting,
    selectedFiles,
    geometryType,
    handleGeometryChange,
    handleReset,
    onSubmit,
    activeStep,
    gdalInfo: gdal.gdalInfo,
    gdalFailed: gdal.gdalFailed,
    gdalSkipped: gdal.gdalSkipped,
    datasetName,
    datasetId,
    extracting: gdal.extracting,
    uploading,
    progress,
    status,
    errorMessage,
  }
}
